/**
 * CANONICAL support taxonomy — mirror of apna-yatri-backend/src/data/supportEnums.ts. Keep in sync.
 * The admin ticket-type filter previously used codes (payment_issue|ride_issue|…) that matched ZERO
 * tickets (backend writes fare|trip|…). Everyone must use THESE codes now.
 */

export const SUPPORT_ISSUE_TYPES = [
  'fare', 'lost_item', 'safety', 'vehicle', 'app', 'account_deletion', 'trip',
  'driver_payout', 'earnings_dispute', 'cash_not_collected', 'document_rejected',
  'subscription', 'wallet', 'account_suspension', 'rider_behaviour', 'onboarding',
  'general',
] as const;
export type SupportIssueType = (typeof SUPPORT_ISSUE_TYPES)[number];

export const ISSUE_TYPE_LABELS: Record<string, string> = {
  fare: 'Fare / Payment', lost_item: 'Lost Item', safety: 'Safety', vehicle: 'Vehicle',
  app: 'App Issue', account_deletion: 'Account Deletion', trip: 'Trip',
  driver_payout: 'Driver Payout', earnings_dispute: 'Earnings Dispute',
  cash_not_collected: 'Cash Not Collected', document_rejected: 'Document Rejected',
  subscription: 'Subscription', wallet: 'Wallet', account_suspension: 'Account Suspension',
  rider_behaviour: 'Rider Behaviour', onboarding: 'Onboarding', general: 'General',
};
export const issueTypeLabel = (code: string): string => ISSUE_TYPE_LABELS[code] ?? code;

export const TICKET_STATUSES = ['new', 'open', 'in_progress', 'pending_customer', 'on_hold', 'resolved', 'closed'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];
export const STATUS_LABELS: Record<string, string> = {
  new: 'New', open: 'Open', in_progress: 'In Progress', pending_customer: 'Pending Customer', on_hold: 'On hold',
  resolved: 'Resolved', closed: 'Closed',
};

/** Why a ticket was resolved (backend RESOLUTION_CODES); the dashboard must send one to resolve. */
export const RESOLUTION_CODES: Record<string, string> = {
  refunded: 'Refunded', explained: 'Explained', fixed: 'Fixed', driver_warned: 'Driver warned', driver_suspended: 'Driver suspended',
  no_fault_found: 'No fault found', duplicate: 'Duplicate', customer_withdrew: 'Customer withdrew', other: 'Other',
};
/** Phase 2 work queues (from the ticket's topic). */
export const QUEUE_LABELS: Record<string, string> = { safety: 'Safety', rider: 'Rider', driver: 'Driver', payments: 'Payments' };

/** Display labels, incl. codes only the system sets (auto-resolve). */
export const RESOLUTION_LABELS: Record<string, string> = {
    ...RESOLUTION_CODES, no_response: 'No reply from customer (auto)',
    // R2 D10: SOS tickets close with sos_<outcome>
    sos_safe: 'SOS: rider safe', sos_false_alarm: 'SOS: false alarm', sos_police: 'SOS: police involved', sos_medical: 'SOS: medical help', sos_other: 'SOS: other',
};

/** Why the customer gave 1–2 stars (Support v2 §5.10; mirrors backend CSAT_REASONS). */
export const CSAT_REASON_LABELS: Record<string, string> = {
    slow_reply: 'Slow reply', not_solved: 'Not solved', rude_or_unhelpful: 'Rude or unhelpful', wrong_information: 'Wrong information', had_to_repeat: 'Had to repeat myself', other: 'Other',
};
export const isOpenStatus = (s: string): boolean => !['resolved', 'closed'].includes(s);

export const TICKET_PRIORITIES = ['p0_safety', 'urgent', 'high', 'normal', 'low'] as const;
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];
export const PRIORITY_LABELS: Record<string, string> = {
  p0_safety: 'P0 · Safety', urgent: 'Urgent', high: 'High', normal: 'Normal', low: 'Low',
};
/** Chip color per priority (MUI-ish palette keys the components can map). */
export const PRIORITY_COLOR: Record<string, 'error' | 'warning' | 'info' | 'default' | 'success'> = {
  p0_safety: 'error', urgent: 'error', high: 'warning', normal: 'info', low: 'default',
};

export const TICKET_SOURCES = ['app_chat', 'whatsapp', 'ivr_voice', 'email', 'agent_manual', 'bot', 'sos', 'automation'] as const;
export type TicketSource = (typeof TICKET_SOURCES)[number];
